import { useNavigate, useParams } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AuditoriaAauditoresForm,
  AuditoriaAauditoresSchema,
} from "../schemas/Auditoria_auditores/auditoria_auditores";
import { useObrasTopHeaderState } from "../../../obras/presentation/components/hooks/obras_top_header_state";
import { AuditoresLista } from "../../domain/models/auditores_lista";
import { useAuditoresFilters } from "../hooks/Auditoria_auditores/filter";
import { useAuditoresPagination } from "../hooks/Auditoria_auditores/pagination";
import {
  ObraForm,
  ObraSchema,
} from "../../../obras/presentation/schemas/Obras_informacoesgerais/obra";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { isValidFileName } from "../../../../common/utils/isValidFileName";

type LoadAuditoresListaFuncState = {
  IdAuditor?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number;
  pesquisa?: string;
};

export const useAuditoresCadastroViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    reports: {},
    auditor: {
      getAllListagemAuditoresUseCase,
      createAuditoresUseCase,
      updateAuditorUseCase,
      deleteAuditoresUseCase,
      getArquivoAuditoresUseCase,
    },
  } = useDependencyInjection();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [auditoresModal, setAuditoresModal] = useState<boolean>(false);
  const [selectedAuditores, setSelectedAuditores] = useState<
    AuditoresLista | undefined
  >(undefined);
  const [idAuditor, setIdAuditor] = useState<number>(0);
  const navigate = useNavigate();

  //Arquivo
  const [auditoresDeleteModal, setAuditoresDeleteModal] =
    useState<boolean>(false);
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] =
    useState<boolean>(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [arquivo, setArquivo] = useState<string>();

  const hookForm = useForm<ObraForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: { tipo: 0 },
    resolver: zodResolver(ObraSchema),
  });

  const hookFormAuditores = useForm<AuditoriaAauditoresForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AuditoriaAauditoresSchema),
    defaultValues: {
      TipoAuditor: 0,
    },
  });

  const { pushErrorNotification, pushInfoNotification, setCustomLoading } =
    usePageLoader();

  const obrasTopHeaderState: any = useObrasTopHeaderState();
  const filters = useAuditoresFilters();
  const pagination = useAuditoresPagination();

  // #region Funções de carregamento

  const loadAuditoresListaState = useCallback(
    async (funcState?: LoadAuditoresListaFuncState) => {
      try {
        const filterID_Auditor = evaluateParamValue(
          funcState?.IdAuditor,
          filters.IdAuditor,
        );

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ?? pagination.rowsPerPage;
        const filteredTipoPesquisa =
          funcState?.tipoPesquisa ?? filters.tipoPesquisa;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        await getAllListagemAuditoresUseCase.getAllListagemAuditores(
          {
            IdAuditor: filterID_Auditor,
            PesquisarPor: filteredTipoPesquisa,
            Pesquisar: filteredPesquisa,
          },
          filteredPage,
          filteredRowsPerPage,
        );
        pagination.setRowsPerPage(getAllListagemAuditoresUseCase.pageSize);
        pagination.setTotalItems(getAllListagemAuditoresUseCase.totalItems);
        const auditores =
          getAllListagemAuditoresUseCase.items &&
          getAllListagemAuditoresUseCase.items.length > 0
            ? getAllListagemAuditoresUseCase.items[0]
            : null;
        if (auditores) {
          setSelectedAuditores(auditores);
          hookForm.reset();
          if (auditores) {
            if (auditores.TelCelular)
              hookFormAuditores.setValue("TelCelular", auditores.TelCelular);

            if (auditores.TelFixo)
              hookFormAuditores.setValue("TelFixo", auditores.TelFixo);

            hookFormAuditores.setValue("Nome", auditores.Nome);
            hookFormAuditores.setValue("Cidade", auditores.Cidade);
            hookFormAuditores.setValue(
              "OutrasInformacoes",
              auditores.OutrasInformacoes,
            );
            hookFormAuditores.setValue("nomeArquivo1", auditores.nomeArquivo1);
            hookFormAuditores.setValue("nomeArquivo2", auditores.nomeArquivo2);
            hookFormAuditores.setValue("nomeArquivo3", auditores.nomeArquivo3);
            hookFormAuditores.setValue("nomeArquivo4", auditores.nomeArquivo4);
            hookFormAuditores.setValue("nomeArquivo5", auditores.nomeArquivo5);
            hookFormAuditores.setValue("TipoAuditor", auditores.TipoAuditor ?? 0);
          }
        }
      } catch (e: unknown) {
        throw new Error("Falha ao buscar auditores!");
      }
    },
    [
      filters.IdAuditor,
      filters.pesquisa,
      filters.tipoPesquisa,
      getAllListagemAuditoresUseCase,
      hookForm,
      hookFormAuditores,
      pagination,
    ],
  );

  //#endregion
  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      if (id) {
        setIdAuditor(Number(id));
        jobs.push(loadAuditoresListaState({ IdAuditor: Number(id) }));
      }

      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      if (id) {
        setIdAuditor(Number(id));
        jobs.push(loadAuditoresListaState({ IdAuditor: Number(id) }));
      }

      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
        { isAutoFinish: true },
      );
    } finally {
      setLoading(false);
    }
  }, [id, loadAuditoresListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handleVoltarOpenState = useCallback(() => {
    navigate(`/Auditores_lista/`);
  }, [navigate]);

  const handleAuditorlSubmit = useCallback(
    async (form: AuditoriaAauditoresForm) => {
      setSaving(true);
      setCustomLoading(true);

      try {
        if (idAuditor) {
          await updateAuditorUseCase.updateAuditor(
            idAuditor,
            form.Nome ?? "",
            form.TelFixo ?? "",
            form.TelCelular ?? "",
            form.Email ?? "",
            form.OutrasInformacoes ?? "",
            form.Cidade ?? "",
            form.Arquivo1 ?? null,
            form.Arquivo2 ?? null,
            form.Arquivo3 ?? null,
            form.Arquivo4 ?? null,
            form.Arquivo5 ?? null,
            form.limparArquivo1,
            form.limparArquivo2,
            form.limparArquivo3,
            form.limparArquivo4,
            form.limparArquivo5,
            form.TipoAuditor ?? 0
          );
          pushInfoNotification("Sucesso", "Auditor atualizado com sucesso!", {
            isAutoFinish: true,
          });
        } else {
          await createAuditoresUseCase.createAuditores(
            form.Nome ?? "",
            form.TelFixo ?? "",
            form.TelCelular ?? "",
            form.Email ?? "",
            form.OutrasInformacoes ?? "",
            form.Cidade ?? "",
            form.Arquivo1 ?? null,
            form.Arquivo2 ?? null,
            form.Arquivo3 ?? null,
            form.Arquivo4 ?? null,
            form.Arquivo5 ?? null,
            form.TipoAuditor ?? 0
          );
          pushInfoNotification("Sucesso", "Auditor criado com sucesso!", {
            isAutoFinish: true,
          });
        }
        await handleVoltarOpenState();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar/atualizar Auditores!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
        setAuditoresModal(false);
        setCustomLoading(false);
      }
    },
    [
      createAuditoresUseCase,
      handleVoltarOpenState,
      idAuditor,
      pushErrorNotification,
      pushInfoNotification,
      setCustomLoading,
      updateAuditorUseCase,
    ],
  );

  const handleDeleteConfirmaAuditores = useCallback(async () => {
    if (selectedAuditores) {
      try {
        setSaving(true);
        await deleteAuditoresUseCase.deleteAuditores(
          selectedAuditores.IdAuditor,
        );
        await loadAuditoresListaState();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
        setAuditoresDeleteModal(false);
      }
    }
  }, [
    selectedAuditores,
    deleteAuditoresUseCase,
    loadAuditoresListaState,
    pushErrorNotification,
  ]);

  //#endregion

  //#region ARQUIVOS

  const handleDownloadFile = useCallback(
    async (filename: string, imagePosition: number) => {
      if (id) {
        try {
          setCustomLoading(true);

          await getArquivoAuditoresUseCase.getArquivo(
            Number(id),
            imagePosition,
            filename,
          );
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao fazer download do arquivo!"),
            { isAutoFinish: true },
          );
        } finally {
          setCustomLoading(false);
        }
      }
    },
    [getArquivoAuditoresUseCase, id, pushErrorNotification, setCustomLoading],
  );

  const handleArquivoDeleteOpenState = useCallback(
    (
      limparArquivos: string | null,
      arquivo: string | null,
      openState: boolean,
    ) => {
      if (openState && limparArquivos && arquivo) {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(limparArquivos);
        setArquivo(arquivo);
      } else {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(undefined);
        setArquivo(undefined);
      }
    },
    [],
  );

  const handleFileChange = useCallback(
    (fileId: number | null) => {
      if (fileId && selectedAuditores) {
        switch (fileId) {
          case 1:
            setSelectedAuditores({ ...selectedAuditores, nomeArquivo1: "" });
            hookFormAuditores.setValue("limparArquivo1", true);
            break;

          case 2:
            setSelectedAuditores({ ...selectedAuditores, nomeArquivo2: "" });
            hookFormAuditores.setValue("limparArquivo2", true);
            break;

          case 3:
            setSelectedAuditores({ ...selectedAuditores, nomeArquivo3: "" });
            hookFormAuditores.setValue("limparArquivo3", true);
            break;

          case 4:
            setSelectedAuditores({ ...selectedAuditores, nomeArquivo4: "" });
            hookFormAuditores.setValue("limparArquivo4", true);
            break;

          case 5:
            setSelectedAuditores({ ...selectedAuditores, nomeArquivo5: "" });
            hookFormAuditores.setValue("limparArquivo5", true);
            break;
        }
      }
    },
    [selectedAuditores, hookFormAuditores],
  );

  // const handleConfirmarDeletarArquivo = useCallback(async () => {
  //   if (limparArquivo && arquivo) {
  //       handleDeleteFile(limparArquivo, arquivo)
  //       setOpenDeleteArquivoModal(false)
  //       setArquivo(undefined)
  //       setLimparArquivo(undefined)
  //   }
  // }, [limparArquivo, arquivo, handleDeleteFile]);

  //#endregion

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (idAuditor) {
          changeHistory.handleOpenChangeHistory(
            idAuditor,
            LogTipoTabela.AUDITOR,
            true,
          );
        }
      }
    },
    [idAuditor, changeHistory],
  );

  return {
    loading,
    saving,
    errorMessage,
    hookForm,
    obrasTopHeaderState,
    auditoresModal,
    selectedAuditores,
    filters,
    pagination,
    auditoresDeleteModal,
    openDeleteArquivoModal,
    hookFormAuditores,
    idAuditor,
    changeHistory,
    handleOpenChangeHistory,
    handleVoltarOpenState,
    handleReload,
    handleAuditorlSubmit,
    handleDeleteConfirmaAuditores,
    handleFileChange,
    handleArquivoDeleteOpenState,
    handleDownloadFile,
  };
};
